<template lang="html">
  <div
    :id="expressionId"
    class="expression-input"
    ref="expContainer"
    @keydown.up.stop="onPressUp"
    @keydown.down.stop="onPressDown"
    @keydown.enter.stop="onPressEnter"
    v-if="isReady"
  >
    <ExpressionInput :ports="setupPorts" :flags="flags" />
  </div>
</template>

<script lang="js">
  import store from "@/store";
  import { v4 as uuidv4 } from "uuid";
  import i18n from "@/plugins/i18n";
  import iconsUri from "@/views/drag-drop-pages/components/utils/icons-uri";
  import ExpressionInput from "@/components/ExpressionInput.elm";
  import * as WrapElm from "@/components/utils/elmWrapper.js";

  export default  {
    name: "expressions",
    components: { ExpressionInput: WrapElm(ExpressionInput.Elm.ExpressionInput) },
    props: {
      id: {
        type: String
      },
      dataElements: {
        type: Object
      },
      // selectedSource is available only if expression is local
      selectedSource: {
        type: Object,
        default: null
      },
      // dataType: comparable, any, string, number, bool, date ...
      dataType: {
        type: String,
        default: null
      },
      // initial expression value
      initialExpression: {
        type: Object,
        default: null
      },
      isListExpression: {
        type: Boolean,
        default: false
      },
      // to indicate if expression is global
      isGlobal: {
        type: Boolean,
        default: false
      },
      contentType: {
        type: String,
        default: null
      }
    },
    watch: {
      isListExpression() {
        if(this.isReady && this.prevIsListExpression !== this.isListExpression) {
          this.resetIsReady();
        }
      },
      dataType() {
        if(this.isReady) {
          this.resetIsReady();
        }
      }
    },
    computed: {
      expressionId() {
        return this.id || `expression-input-${uuidv4()}`;
      },
      messages() {
        return store.state.messages;
      },
      flags() {
        return {
          dataElements: this.dataElements,
          uuid: this.selectedSource? this.selectedSource.id : null,
          isListExpression: this.isListExpression,
          isGlobal: this.isGlobal, // false means is local expression
          dataType: this.dataType,
          initialExpression: this.initialExpression,
          veevaMessages: this.messages,
          i18nMessages: this.i18nMessages,
          iconsUri: iconsUri.utilitySpriteUri,
          contentType: this.contentType
        };
      },
      i18nMessages() {
        const i18Messages = i18n.messages;
        const locale = i18n.locale || "en_US";
        const defaultMessages = i18Messages[i18n.fallbackLocale] || i18Messages.en || {};
        const messages = i18Messages[locale] || i18Messages[locale.split("_")[0]] || {};
        return {...defaultMessages, ...messages};
      }
    },
    mounted () {
      this.$nextTick(() => {
        this.resetIsReady();
      });
    },
    data () {
      return {
        isReady: false,
        prevIsListExpression: null,
        selectedId: -1
      }
    },
    methods: {
      removeSelectedClass(index, expList) {
        if(index >= 0) {
          expList.children[index].classList.remove("singleSelect__option--highlight");
        }
      },
      selectAnItem(index, expList) {
        this.selectedId = index;
        expList.children[index].classList.add("singleSelect__option--highlight");
        expList.children[index].scrollIntoView(false);
      },
      onPressUp() {
        const expList = Array.prototype.find.call(this.$refs.expContainer.getElementsByTagName("ul"),
          (e) => e.parentElement.style.display === "block");
        if (!expList) {
          return;
        }

        if (this.selectedId  >= expList.children.length) {
          this.selectedId = -1;
        }

        if (this.selectedId > 0) {
          this.removeSelectedClass(this.selectedId, expList);
          const index = this.selectedId - 1;
          if (index >= 0) {
            this.selectAnItem(index, expList);
          }
        }
      },
      onPressDown() {
        const expList = Array.prototype.find.call(this.$refs.expContainer.getElementsByTagName("ul"),
          (e) => e.parentElement.style.display === "block");
        if (!expList) {
          return;
        }

        if (this.selectedId  >= expList.children.length) {
          this.selectedId = -1;
        }

        if (this.selectedId < expList.children.length - 1) {
          this.removeSelectedClass(this.selectedId, expList);
          this.selectAnItem(this.selectedId + 1, expList);
        }
      },
      onPressEnter() {
        const expList = Array.prototype.find.call(this.$refs.expContainer.getElementsByTagName("ul"),
          (e) => e.parentElement.style.display === "block");
        if (!expList) {
          return;
        }

        if (this.selectedId > -1) {
          expList.children[this.selectedId].click();
        }
        this.selectedId = -1;
      },
      resetIsReady() {
        this.isReady = false;
        this.$nextTick(() => {
          this.prevIsListExpression = this.isListExpression;
          this.isReady = true;
        });
      },
      sendUpdate(data) {
        this.selectedId = -1;
        this.$emit("on-expression-field-valid", {
          id: this.id,
          status: Boolean(data && data.expression),
          confExpression: data && data.expression ? data.expression : {}
        });
        if(data) {
          this.$emit("on-object-tags-found", data.objectTags);
        }
      },
      sendToLog() {},
      setupPorts(ports) {
        ports.sendUpdate.subscribe(this.sendUpdate);
        // ports.sendToLog.subscribe(this.sendToLog); // Uncomment once sentToLog from elm is ready
      }
    }
  }
</script>

<style lang="scss">
  .singleSelect__option--highlight {
    background-color: var(--HEADER_GRAY);
  }
  $border-style: 1px solid var(--LIGHT_GRAY);
  $exp-font-size: 0.75rem;
  @mixin exp-input-selection {
    width: 100%;
    background-color: var(--WHITE);
    border: $border-style;
    color: var(--DARK_BLUE);
    padding: 0 2rem 0 0.75rem;
    border-radius: 0.25rem;
    line-height: 1.875rem;
    min-height: calc(1.875rem + 2px);
    transition: border 0.1s linear, background-color 0.1s linear;
    display: inline-block;
    box-sizing: border-box;
  }
  @mixin exp-icon {
    height: 1rem;
    width: 1rem;
    fill: var(--DARK_GREY);
    cursor: pointer;
    margin-right: 0.3rem;
  }
  .expression-input {
    width: 100%;
    .input-wrapper {
      width: 100%;
      position: relative;
    }
    .m-result-list {
      border: $border-style;
      box-shadow: 0 2px 3px 0 rgb(0 0 0 / 16%);
      color: var(--DARK_BLUE);
      background: var(--WHITE);
      min-width: 12rem;
      width: calc(100% - 2px);
      position: absolute;
      z-index: 7000;
      margin-bottom: 0.125rem;
      border-radius: 0.25rem;
      font-size: $exp-font-size;
      line-height: 1.5;
      overflow-y: auto;
      max-height: 13rem;
      ul {
        margin-bottom: 0;
        li {
          box-sizing: border-box;
          display: list-item;
          padding: 0;
          margin: 0;
          overflow: hidden;
          color: var(--LIST_RESULTS);
          .m-text-items {
            overflow: hidden;
            padding: 0.75rem;
          }
          .m-list-option-icon {
            display: flex;
            min-width: 26px;
            float: left;
            padding-top: 16px;
            padding-left: 8px;
          }
          :hover {
            background-color: var(--HEADER_GRAY);
            text-decoration: none;
            cursor: pointer;
          }
        }
      }
    }
    .form-input {
      width: 100%;
      padding: 0.375rem 0.75rem;
      font-size: $exp-font-size;
      font-weight: 400;
      line-height: 1.5;
      color: var(--DARK_GREY);
      background-color: var(--WHITE);
      border: $border-style;
      border-radius: 0.25rem;
    }
    .config-expression {
      border: $border-style;
      width: 100%;
      box-sizing: border-box;
      position: relative;
      padding: 0.3rem 0.9rem 0.3rem 0.5rem;
      font-size: $exp-font-size;
      .exp-content {
        display: flex;
        justify-content: space-between;
        .selection {
          width: 100%;
          .content .config-field {
            border: $border-style;
            width: 100%;
            box-sizing: border-box;
            position: relative;
            padding: 0.3rem 0.4rem 0.8rem 0.4rem;
            font-size: $exp-font-size;
            border: none;
            .row-data {
              display: flex;
              flex-direction: column;
              padding: 0.3rem;
              .list-exp-item {
                width: 100%;
                display: flex;
                align-items: center;
                .list-drag-item-icon {
                  height: 1.8rem;
                  width: 1rem;
                  fill: #939495;
                  cursor: move;
                  margin-right: -1rem;
                  transform: translateX(-1.1rem);
                  padding-left: 0.1rem;
                  padding-right: 0.1rem;
                }
              }
              .exp-content {
                width: 100%;
              }
              .exp-label {
                white-space: nowrap;
                margin-right: 0.5rem;
              }
              .exp-operator-label {
                width: 100%;
              }
              .label-operator {
                font-size: 1.5rem;
                text-align: center;
                width: 100%;
              }
            }
          }
        }
      }
    }
    .m-search-container {
      position: relative;
    }
    .m-input {
      @include exp-input-selection;
    }
    .m-input-search-icon {
      position: absolute;
      bottom: 0.5rem;
      right: 0.5rem;
    }
    .m-input-search-icon,
    .m-close-selection-icon {
      @include exp-icon;
    }
    .exp-content {
      position: relative;
      .config-expression .exp-content .selection .content {
        margin-top: 0.5rem;
      }
      .close-icon-wrapper {
        height: 1rem;
        width: 1rem;
        position: absolute;
        top: 0;
        right: 0;

        .close-icon {
          @include exp-icon;
          position: absolute;
          top: 0;
          right: 0;
          margin: 0;
        }
      }
      .m-search-box .m-selection {
        @include exp-input-selection;
        padding: 0.2rem;
        .m-item-selected {
          border: $border-style;
          display: flex;
          justify-content: space-between;
          align-items: center;
          box-shadow: none;
          max-height: 24px;
          line-height: 1.8;
          font-size: 0.9rem;
          padding-left: 4px;
          .m-selection-label {
            display: inline-block;
          }
          .m-close-icon-wrapper {
            padding: 0;
            background: none;
            border: none;

            .m-close-selection-icon {
              margin: 0 0.25rem;
            }
          }

          .m-content-selection {
            font-size: $exp-font-size;
            line-height: 2;
            margin: 0 2.5rem 0 0.25rem;
          }
        }
      }
    }
  }
</style>
